import type { Pagination } from '../types/api';
import type { AxiosError } from 'axios';
import api from '../lib/api';

export interface ApiError {
  status: 'error';
  error: string;
  message?: string;
  errors?: Record<string, unknown>;
}

export function unwrap<T>(response: { data: { data?: T } }): T {
  return response.data.data as T;
}

export function errorCodeFrom(err: unknown): string | null {
  const code = (err as AxiosError<ApiError>)?.response?.data?.error;
  return typeof code === 'string' ? code : null;
}

export function errorMessageFrom(err: unknown): string | null {
  const data = (err as AxiosError<ApiError>)?.response?.data;
  return data?.message ?? data?.error ?? null;
}

export function fieldErrorsFrom(err: unknown): Record<string, string> | null {
  const errors = (err as AxiosError<ApiError>)?.response?.data?.errors;
  if (!errors) return null;
  const mapped: Record<string, string> = {};
  for (const [k, v] of Object.entries(errors)) {
    if (Array.isArray(v)) mapped[k] = v[0];
    else if (typeof v === 'string') mapped[k] = v;
  }
  return mapped;
}

export { api };

/**
 * The courses and programs endpoints report `total_pages` / `total_items`,
 * while every other list endpoint — and the shared `Pagination` type the
 * paginators read — uses `pages` / `total`. Reading `.pages` off the raw
 * payload gave `undefined`, so `pages > 1` was false and the pager never
 * rendered: a library with 15 courses showed 12 and no way to reach the rest
 * (Aleix, 2026-09-10). Normalise here rather than change a public response
 * shape mid-flight.
 */
export function normalizePagination(raw: unknown): Pagination {
  const p = (raw ?? {}) as Record<string, number | undefined>;
  return {
    page: p.page ?? 1,
    per_page: p.per_page ?? 0,
    total: p.total ?? p.total_items ?? 0,
    pages: p.pages ?? p.total_pages ?? 1,
  };
}
